import { getVapidPublicKey, subscribePush, unsubscribePush } from "../api/pushApi";

/**
 * Web Push 클라이언트 흐름.
 *
 *   enablePush()  : SW 등록 확인 → 알림 권한 요청 → 백엔드 VAPID 키 받음
 *                  → PushManager.subscribe → 백엔드에 subscription 저장.
 *   disablePush() : 현재 subscription 을 백엔드에서 해제 + 브라우저에서 unsubscribe.
 *   getPushStatus(): 현재 알림 권한 + subscription 존재 여부 반환.
 *
 * 브라우저 지원 안 하는 환경에선 모든 함수가 graceful 하게 false/null 반환.
 */

const isSupported = () =>
  typeof navigator !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

// VAPID public key (base64url) → Uint8Array. Web Push API 가 요구하는 포맷.
const urlBase64ToUint8Array = base64String => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

const subscriptionToJson = sub => {
  const json = sub.toJSON();
  return {
    endpoint: json.endpoint,
    p256dh: json.keys && json.keys.p256dh,
    auth: json.keys && json.keys.auth
  };
};

export const getPushStatus = async () => {
  if (!isSupported()) return { supported: false, permission: "unsupported", subscribed: false };
  const permission = Notification.permission;
  let subscribed = false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    subscribed = !!sub;
  } catch (e) {
    // ignore
  }
  return { supported: true, permission, subscribed };
};

export const enablePush = async () => {
  if (!isSupported()) {
    throw new Error("이 브라우저는 푸시 알림을 지원하지 않습니다.");
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("알림 권한이 허용되지 않았습니다.");
  }

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const publicKey = await getVapidPublicKey();
    if (!publicKey) throw new Error("서버 VAPID 키를 가져오지 못했습니다.");
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    });
  }
  await subscribePush(subscriptionToJson(sub));
  return true;
};

export const disablePush = async () => {
  if (!isSupported()) return false;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return true;
  try {
    await unsubscribePush(sub.endpoint);
  } catch (e) {
    // 백엔드 실패해도 브라우저 unsubscribe 는 시도
  }
  await sub.unsubscribe();
  return true;
};
